import React, { useState, useEffect } from "react";
import { Mail, ArrowRight, Eye, EyeOff, X } from "lucide-react";
import { api } from "@/lib/api";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Algo salió mal. Intenta de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const REMEMBER_KEY = "uaicard_remember";
function loadRemembered() {
  try {
    const raw = localStorage.getItem(REMEMBER_KEY);
    if (!raw) return null;
    return JSON.parse(decodeURIComponent(atob(raw)));
  } catch { return null; }
}
function saveRemembered(email, password) {
  try { localStorage.setItem(REMEMBER_KEY, btoa(encodeURIComponent(JSON.stringify({ email, password })))); } catch { /* ignore */ }
}
function clearRemembered() {
  try { localStorage.removeItem(REMEMBER_KEY); } catch { /* ignore */ }
}
// Cuentas guardadas en este dispositivo (varias).
const ACCOUNTS_KEY = "zv_saved_accounts";
function loadAccounts() {
  try {
    const list = JSON.parse(decodeURIComponent(atob(localStorage.getItem(ACCOUNTS_KEY) || ""))) || [];
    return Array.isArray(list) ? list : [];
  } catch {
    const r = loadRemembered();
    return r?.email ? [{ email: r.email, password: r.password || "" }] : [];
  }
}
function storeAccounts(list) {
  try { localStorage.setItem(ACCOUNTS_KEY, btoa(encodeURIComponent(JSON.stringify(list.slice(0, 8))))); } catch { /* ignore */ }
}
function upsertAccount(email, password, name) {
  const list = loadAccounts().filter((a) => a.email.toLowerCase() !== email.toLowerCase());
  storeAccounts([{ email, password, name: name || "" }, ...list]);
}

function SavedAccounts({ accounts, current, onPick, onRemove }) {
  if (!accounts.length) return null;
  return (
    <div className="mb-5" data-testid="saved-accounts">
      <div className="text-[10px] uppercase tracking-[0.25em] text-nardo-500 mb-2">Cuentas guardadas</div>
      <div className="space-y-1.5">
        {accounts.map((a) => (
          <div key={a.email} className={`flex items-center gap-2 rounded-lg border px-3 py-2 transition-colors ${current === a.email ? "border-navy bg-navy/5" : "border-nardo-200 hover:border-navy/40"}`}>
            <button type="button" onClick={() => onPick(a)} className="flex items-center gap-2 flex-1 min-w-0 text-left" data-testid={`saved-account-${a.email}`}>
              <span className="w-8 h-8 rounded-full bg-navy text-white text-sm font-bold flex items-center justify-center shrink-0">{(a.name || a.email)[0].toUpperCase()}</span>
              <span className="min-w-0"><span className="block text-sm font-semibold text-navy truncate">{a.name || a.email}</span>{a.name && <span className="block text-[11px] text-nardo-500 truncate">{a.email}</span>}</span>
            </button>
            <button type="button" onClick={() => onRemove(a.email)} title="Eliminar de este dispositivo" className="p-1.5 text-nardo-400 hover:text-red-600 shrink-0" data-testid={`saved-account-remove-${a.email}`}><X className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function PasswordField({ value, onChange, name, autoComplete, testId, inputCls }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        type={show ? "text" : "password"}
        required
        name={name}
        autoComplete={autoComplete}
        value={value}
        onChange={onChange}
        data-testid={testId}
        className={`${inputCls} pr-10`}
        placeholder="••••••••"
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        data-testid={`${testId}-toggle`}
        aria-label={show ? "Ocultar contraseña" : "Mostrar contraseña"}
        title={show ? "Ocultar contraseña" : "Mostrar contraseña"}
        className="absolute right-0 bottom-2 text-nardo-400 hover:text-navy transition-colors p-1"
      >
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
}

export default function EmailAuthPanel({ onSuccess, onBack, onGoogle, portalLabel }) {
  const [accounts, setAccounts] = useState(loadAccounts);
  const remembered = accounts[0] || loadRemembered();
  const [mode, setMode] = useState("login"); // login | register | forgot | reset
  const [email, setEmail] = useState(remembered?.email || "");
  const [password, setPassword] = useState(remembered?.password || "");
  const [name, setName] = useState("");
  const [school, setSchool] = useState("");
  const [remember, setRemember] = useState(remembered ? true : true);
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);

  // Recuerda el correo y la contraseña MIENTRAS se escriben (no solo al iniciar sesión con éxito),
  // para que queden prellenados aunque el intento falle, se cierre sesión o se cierre la app.
  const pickAccount = (a) => { setEmail(a.email); setPassword(a.password || ""); setError(null); };
  const removeAccount = (em) => {
    const list = loadAccounts().filter((a) => a.email !== em);
    storeAccounts(list); setAccounts(list); clearRemembered();
    if (email === em) { setEmail(""); setPassword(""); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null); setInfo(null); setBusy(true);
    try {
      if (mode === "login") {
        const { data } = await api.post("/auth/login", { email, password, remember });
        clearRemembered();
        if (remember) upsertAccount(email.trim(), password, data.user?.name);
        onSuccess(data.user);
      } else if (mode === "register") {
        const { data } = await api.post("/auth/register", { email, password, name, school_name: school });
        saveRemembered(email, password);
        onSuccess(data.user);
      } else if (mode === "forgot") {
        const { data } = await api.post("/auth/forgot-password", { email });
        setResetCode("");
        setInfo(data.message || "Si la cuenta existe, te enviamos un código a tu correo. Revisa tu bandeja (y spam).");
        setMode("reset");
      } else if (mode === "reset") {
        await api.post("/auth/reset-password", { email, token: resetCode, new_password: newPassword });
        setInfo("Contraseña actualizada. Inicia sesión.");
        setPassword(""); setMode("login");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const titles = {
    login: "Iniciar sesión",
    register: "Crear cuenta",
    forgot: "Recuperar contraseña",
    reset: "Nueva contraseña",
  };
  const inputCls = "mt-1 w-full border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none text-navy";

  return (
    <div data-testid="email-auth-panel">
      <div className="flex items-center gap-2 mb-3">
        <Mail className="w-4 h-4 text-navy" />
        <div className="text-[10px] uppercase tracking-[0.3em] text-navy">Correo y contraseña</div>
      </div>
      {portalLabel && mode === "login" && (
        <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-navy to-blue-600 text-white px-4 py-1.5 text-xs font-semibold shadow" data-testid="portal-target-label">
          <ArrowRight className="w-3.5 h-3.5" /> Ingresarás al {portalLabel}
        </div>
      )}
      <h2 className="font-display font-bold text-2xl text-navy mb-1">{titles[mode]}</h2>
      <p className="text-sm text-nardo-500 mb-6">
        {mode === "register" ? "Acceso gratuito, sin código de licencia." : "Acceso directo a tu colegio."}
      </p>

      {error && <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-md p-3" data-testid="email-auth-error">{error}</div>}
      {info && <div className="mb-4 bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm rounded-md p-3" data-testid="email-auth-info">{info}</div>}

      {mode === "login" && <SavedAccounts accounts={accounts} current={email} onPick={pickAccount} onRemove={removeAccount} />}
      <form onSubmit={submit} className="space-y-4">
        {mode === "register" && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-nardo-500">Nombre</label>
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="reg-name-input" className={inputCls} placeholder="Tu nombre" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-nardo-500">Colegio</label>
              <input value={school} onChange={(e) => setSchool(e.target.value)} data-testid="reg-school-input" className={inputCls} placeholder="Mi Colegio" />
            </div>
          </div>
        )}

        {mode !== "reset" && (
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-nardo-500">Correo</label>
            <input type="email" required name="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="email-input" className={inputCls} placeholder="tu@correo.com" />
          </div>
        )}

        {(mode === "login" || mode === "register") && (
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-nardo-500">Contraseña</label>
            <PasswordField value={password} onChange={(e) => setPassword(e.target.value)} name="password" autoComplete="current-password" testId="password-input" inputCls={inputCls} />
          </div>
        )}

        {mode === "reset" && (
          <>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-nardo-500">Correo</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="reset-email-input" className={inputCls} placeholder="tu@correo.com" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-nardo-500">Código de recuperación</label>
              <input required value={resetCode} onChange={(e) => setResetCode(e.target.value.toUpperCase())} data-testid="reset-code-input" className={`${inputCls} font-mono tracking-widest`} placeholder="ABC123" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-nardo-500">Nueva contraseña</label>
              <PasswordField value={newPassword} onChange={(e) => setNewPassword(e.target.value)} name="new-password" autoComplete="new-password" testId="new-password-input" inputCls={inputCls} />
            </div>
          </>
        )}

        {mode === "login" && (
          <label className="flex items-center gap-2 text-sm text-nardo-600 cursor-pointer">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="remember-checkbox" className="accent-navy w-4 h-4" />
            Guardar esta cuenta en el dispositivo
          </label>
        )}

        <button type="submit" disabled={busy} data-testid="email-auth-submit"
          className="w-full group inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-light text-white py-3 rounded-md transition-colors font-medium disabled:opacity-50">
          {busy ? "Procesando…" : titles[mode]}
          {!busy && <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />}
        </button>
      </form>

      {onGoogle && (mode === "login" || mode === "register") && (
        <>
          <div className="flex items-center gap-3 my-5">
            <span className="flex-1 h-px bg-nardo-200" />
            <span className="text-[10px] uppercase tracking-[0.25em] text-nardo-400">o</span>
            <span className="flex-1 h-px bg-nardo-200" />
          </div>
          <button onClick={() => onGoogle()} data-testid="email-panel-google"
            className="w-full inline-flex items-center justify-center gap-3 border border-nardo-300 hover:border-navy text-navy py-3 rounded-md transition-colors font-medium">
            <svg width="18" height="18" viewBox="0 0 24 24" className="shrink-0"><path fill="#112240" d="M21.35 11.1H12v2.9h5.35c-.23 1.42-1.7 4.18-5.35 4.18-3.22 0-5.85-2.66-5.85-5.94S8.78 6.3 12 6.3c1.84 0 3.07.78 3.78 1.45l2.58-2.49C16.83 3.96 14.66 3 12 3 6.99 3 3 6.99 3 12s3.99 9 9 9c5.2 0 8.64-3.65 8.64-8.79 0-.59-.07-1.05-.16-1.51z"/></svg>
            Entrar con Google
          </button>
        </>
      )}

      <div className="mt-5 flex flex-col gap-2 text-sm">
        {mode === "login" && (
          <>
            <button onClick={() => { setMode("forgot"); setError(null); setInfo(null); }} className="text-nardo-500 hover:text-navy text-left" data-testid="switch-forgot">¿Olvidaste tu contraseña?</button>
          </>
        )}
        {mode === "register" && (
          <button onClick={() => { setMode("login"); setError(null); setInfo(null); }} className="text-navy hover:underline text-left" data-testid="switch-login">¿Ya tienes cuenta? Inicia sesión</button>
        )}
        {(mode === "forgot" || mode === "reset") && (
          <button onClick={() => { setMode("login"); setError(null); setInfo(null); }} className="text-navy hover:underline text-left" data-testid="switch-login-2">← Volver a iniciar sesión</button>
        )}
        <button onClick={onBack} className="text-xs uppercase tracking-[0.25em] text-nardo-500 hover:text-navy text-left mt-2" data-testid="email-auth-back">← Cambiar opción</button>
      </div>
    </div>
  );
}
